import { useCallback, useMemo, useState } from 'react';

export type RowSelection = {
  selectedIds: ReadonlySet<string>;
  isSelected: (id: string) => boolean;
  isAllSelected: boolean;
  isIndeterminate: boolean;
  toggleRow: (id: string, checked: boolean) => void;
  toggleAll: (checked: boolean) => void;
  clear: () => void;
};

export function useRowSelection(rows: { id: string }[]): RowSelection {
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  const isSelected = useCallback(
    (id: string) => selectedIds.has(id),
    [selectedIds],
  );

  const isAllSelected = useMemo(
    () => rows.length > 0 && rows.every((r) => selectedIds.has(r.id)),
    [rows, selectedIds],
  );

  const isIndeterminate = useMemo(
    () => !isAllSelected && rows.some((r) => selectedIds.has(r.id)),
    [rows, selectedIds, isAllSelected],
  );

  const toggleRow = useCallback((id: string, checked: boolean) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(id);
      else next.delete(id);
      return next;
    });
  }, []);

  const toggleAll = useCallback(
    (checked: boolean) => {
      setSelectedIds(checked ? new Set(rows.map((r) => r.id)) : new Set());
    },
    [rows],
  );

  const clear = useCallback(() => setSelectedIds(new Set()), []);

  return {
    selectedIds,
    isSelected,
    isAllSelected,
    isIndeterminate,
    toggleRow,
    toggleAll,
    clear,
  };
}
